<template>
  <v-app-bar elevation="2" color="primary-darken-2">
    <v-app-bar-nav-icon @click="toggleSidebar" color="white"></v-app-bar-nav-icon>
    
    <v-app-bar-title class="text-white font-weight-bold">
      {{ currentPageTitle }}
    </v-app-bar-title>
    
    <v-spacer></v-spacer>
    
    <v-menu v-if="user" location="bottom">
      <template v-slot:activator="{ props }">
        <v-btn
          v-bind="props"
          variant="text"
          color="white"
        >
          <span class="ms-2">{{ userDisplayName }}</span>
          <v-icon color="white">mdi-chevron-down</v-icon>
        </v-btn>
      </template>
      
      <v-list>
        <v-list-subheader v-if="departments.length > 1">تبديل القسم</v-list-subheader>
        <v-list-item
          v-for="department in departments"
          :key="department.department_id"
          @click="handleSwitchDepartment(department.department_id)"
          :class="{ 'bg-primary-lighten-5': department.department_id === selectedDepartment?.department_id }"
        >
          <template v-slot:prepend>
            <v-icon 
              :color="department.department_id === selectedDepartment?.department_id ? 'primary' : 'grey'" 
              class="ms-2"
            >
              {{ department.department_id === selectedDepartment?.department_id ? 'mdi-check-circle' : 'mdi-office-building' }}
            </v-icon>
          </template>
          <v-list-item-title>{{ department.department?.name || `القسم ${department.department_id}` }}</v-list-item-title>
        </v-list-item>
        
        <v-divider v-if="departments.length > 1" class="my-2"></v-divider>
        
        <v-list-item @click="$router.push('/dashboard/profile')">
          <template v-slot:prepend>
            <v-icon color="primary" class="ms-2">mdi-account-circle-outline</v-icon>
          </template>
          <v-list-item-title>الملف الشخصي</v-list-item-title>
        </v-list-item>
        
        <v-list-item @click="showChangePasswordDialog = true">
          <template v-slot:prepend>
            <v-icon color="primary" class="ms-2">mdi-lock-reset</v-icon>
          </template>
          <v-list-item-title>تغيير كلمة المرور</v-list-item-title>
        </v-list-item>
        
        <v-divider class="my-2"></v-divider>
        
        <v-list-item @click="handleLogout">
          <template v-slot:prepend>
            <v-icon color="primary" class="ms-2">mdi-logout</v-icon>
          </template>
          <v-list-item-title>تسجيل الخروج</v-list-item-title>
        </v-list-item>
      </v-list>
    </v-menu>
  </v-app-bar>
  
  <!-- Change Password Dialog -->
  <v-dialog v-model="showChangePasswordDialog" max-width="500px">
    <v-card>
      <v-card-title class="d-flex justify-space-between align-center">
        <span>تغيير كلمة المرور</span>
        <v-btn icon variant="text" @click="closeChangePasswordDialog">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>
      
      <v-divider></v-divider>
      
      <v-card-text class="pt-4">
        <v-form ref="changePasswordForm" v-model="passwordFormValid" @submit.prevent="changePassword">
          <v-text-field
            v-model="passwordForm.current_password"
            label="كلمة المرور الحالية"
            :type="showCurrentPassword ? 'text' : 'password'"
            variant="outlined"
            class="mb-4"
            required
            :rules="[v => !!v || 'كلمة المرور الحالية مطلوبة']"
            prepend-inner-icon="mdi-lock"
            :append-inner-icon="showCurrentPassword ? 'mdi-eye-off' : 'mdi-eye'"
            @click:append-inner="showCurrentPassword = !showCurrentPassword"
          ></v-text-field>
          
          <v-text-field
            v-model="passwordForm.new_password"
            label="كلمة المرور الجديدة"
            :type="showNewPassword ? 'text' : 'password'"
            variant="outlined"
            class="mb-4"
            required
            :rules="newPasswordRules"
            prepend-inner-icon="mdi-lock-plus"
            :append-inner-icon="showNewPassword ? 'mdi-eye-off' : 'mdi-eye'"
            @click:append-inner="showNewPassword = !showNewPassword"
          ></v-text-field>
          
          <v-text-field
            v-model="passwordForm.repeat_password"
            label="تأكيد كلمة المرور الجديدة"
            :type="showRepeatPassword ? 'text' : 'password'"
            variant="outlined"
            class="mb-4"
            required
            :rules="repeatPasswordRules"
            prepend-inner-icon="mdi-lock-check"
            :append-inner-icon="showRepeatPassword ? 'mdi-eye-off' : 'mdi-eye'"
            @click:append-inner="showRepeatPassword = !showRepeatPassword"
          ></v-text-field>
          
          <!-- Password Requirements -->
          <v-alert
            type="info"
            variant="tonal"
            density="compact"
            class="mb-4"
          >
            <div class="text-body-2">
              <strong>متطلبات كلمة المرور:</strong>
              <ul class="mt-2">
                <li>على الأقل 8 أحرف</li>
                <li>تحتوي على حرف كبير واحد على الأقل</li>
                <li>تحتوي على حرف صغير واحد على الأقل</li>
                <li>تحتوي على رقم واحد على الأقل</li>
              </ul>
            </div>
          </v-alert>
        </v-form>
      </v-card-text>
      
      <v-divider></v-divider>
      
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn
          variant="text"
          @click="closeChangePasswordDialog"
        >
          إلغاء
        </v-btn>
        <v-btn
          color="primary"
          :loading="changingPassword"
          :disabled="!passwordFormValid"
          @click="changePassword"
        >
          تغيير كلمة المرور
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'

export default {
  name: 'AppHeaderComponent',
  data() {
    return {
      showChangePasswordDialog: false,
      changingPassword: false,
      passwordFormValid: false,
      passwordForm: {
        current_password: '',
        new_password: '',
        repeat_password: ''
      },
      showCurrentPassword: false,
      showNewPassword: false,
      showRepeatPassword: false
    }
  },
  computed: {
    ...mapGetters(['user', 'userRole', 'departments', 'selectedDepartment', 'currentDepartmentName']),
    currentPageTitle() {
      const routeName = this.$route.name
      const titles = {
        'inbox': 'صندوق الوارد',
        'sent': 'صندوق الصادر',
        'archive': 'الأرشيف',
        'drafts': 'المسودات',
        'departments': 'الأقسام',
        'users': 'المستخدمين',
        'templates': 'القوالب',
        'notes': 'الملاحظات',
        'settings': 'الإعدادات',
        'profile': 'الملف الشخصي',
        'mail-priorities': 'أولويات المراسلات',
        'roles': 'إدارة الأدوار',
        'mail-types': 'أنواع المراسلات',
        'file-types': 'أنواع المرفقات',
        'department-jobs': 'المناصب الإدارية',
        'employee-titles': 'المسميات الوظيفية',
        'mail-template-categories': 'فئات قوالب المراسلات'
      }
      return titles[routeName] || 'لوحة التحكم'
    },
    userDisplayName() {
      if (!this.user || !this.user.name) {
        return 'المستخدم'
      }
      const departmentName = this.currentDepartmentName || 'قسم غير محدد'
      return `${this.user.name} - ${departmentName}`
    },
    newPasswordRules() {
      return [
        (v) => !!v || 'كلمة المرور الجديدة مطلوبة',
        (v) => v.length >= 8 || 'كلمة المرور يجب أن تكون على الأقل 8 أحرف',
        (v) => /[A-Z]/.test(v) || 'كلمة المرور يجب أن تحتوي على حرف كبير على الأقل',
        (v) => /[a-z]/.test(v) || 'كلمة المرور يجب أن تحتوي على حرف صغير على الأقل',
        (v) => /\d/.test(v) || 'كلمة المرور يجب أن تحتوي على رقم على الأقل'
      ]
    },
    repeatPasswordRules() {
      return [
        (v) => !!v || 'تأكيد كلمة المرور مطلوب',
        (v) => v === this.passwordForm.new_password || 'كلمة المرور غير متطابقة'
      ]
    }
  },
  methods: {
    ...mapActions(['logout', 'toggleSidebar', 'switchDepartment']),
    async handleLogout() {
      // Check if already logging out to prevent multiple calls
      if (this.$store.state.isLoggingOut) {
        return
      }

      try {
        await this.logout()
        // Use replace instead of push to prevent back navigation
        this.$router.replace('/login')
      } catch (error) {
        console.error('Logout failed:', error)
        // Even if logout fails, try to redirect to login
        this.$router.replace('/login')
      }
    },
    
    async handleSwitchDepartment(departmentId) {
      try {
        const switched = this.switchDepartment(departmentId)
        if (switched) {
          // Optionally refresh the current page to apply new permissions
          this.$router.go(0)
        }
      } catch (error) {
        console.error('Error switching department:', error)
      }
    },
    
    closeChangePasswordDialog() {
      this.showChangePasswordDialog = false;
      this.resetPasswordForm();
    },
    
    resetPasswordForm() {
      this.passwordForm = {
        current_password: '',
        new_password: '',
        repeat_password: ''
      };
      this.showCurrentPassword = false;
      this.showNewPassword = false;
      this.showRepeatPassword = false;
      if (this.$refs.changePasswordForm) {
        this.$refs.changePasswordForm.resetValidation();
      }
    },
    
    async changePassword() {
      // Validate form
      if (!this.$refs.changePasswordForm || !this.$refs.changePasswordForm.validate()) {
        return;
      }
      
      // Check if passwords match
      if (this.passwordForm.new_password !== this.passwordForm.repeat_password) {
        this.$store.dispatch('alerts/showAlert', {
          id: Date.now(),
          type: 'error',
          color: 'error',
          icon: 'mdi-alert-circle',
          timeout: 5000,
          closable: true,
          message: 'كلمة المرور الجديدة وتأكيدها غير متطابقين'
        });
        return;
      }
      
      this.changingPassword = true;
      
      try {
        // Import api client dynamically
        const { default: apiClient } = await import('@/services/api');
        
        // Prepare request data
        const requestData = {
          current_password: this.passwordForm.current_password,
          new_password: this.passwordForm.new_password
        };
        
        // Make API call
        const response = await apiClient.post('/admin/update-password', requestData);
        
        // Show success message
        this.$store.dispatch('alerts/showAlert', {
          id: Date.now(),
          type: 'success',
          color: 'success',
          icon: 'mdi-check-circle',
          timeout: 5000,
          closable: true,
          message: response.data?.message || 'تم تغيير كلمة المرور بنجاح'
        });
        
        // Close dialog and reset form
        this.closeChangePasswordDialog();
        
      } catch (error) {
        console.error('Error changing password:', error);
        
        // Show error message
        const errorMessage = error.response?.data?.message || 
                            error.message || 
                            'حدث خطأ أثناء تغيير كلمة المرور';
        
        this.$store.dispatch('alerts/showAlert', {
          id: Date.now(),
          type: 'error',
          color: 'error',
          icon: 'mdi-alert-circle',
          timeout: 5000,
          closable: true,
          message: errorMessage
        });
      } finally {
        this.changingPassword = false;
      }
    }
  }
}
</script>

<style scoped>
.v-list-item__prepend {
  margin-left: 8px;
  margin-right: 0;
}

.v-app-bar {
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}
</style> 